import {
  Badge as OriginalBadge,
  type BadgeProps as OriginalBadgeProps,
  cn,
  badgeVariants as originalBadgeVariants,
} from '@sr/ui-library-original';
import { type VariantProps, cva, cx } from 'class-variance-authority';

const variantsOverrides = cva('', {
  compoundVariants: [
    {
      class: 'px-2.5 py-1',
      rounded: false,
      style: 'foundation',
    },
    {
      class: '[&_svg]:text-ui-danger',
      style: 'foundation',
      variant: 'destructive',
    },
    {
      class: '[&_svg]:text-ui-success',
      style: 'foundation',
      variant: 'success',
    },
    {
      class: '[&_svg]:text-ui-warning',
      style: 'foundation',
      variant: 'warning',
    },
    {
      class: '[&_svg]:text-ui-info',
      style: 'foundation',
      variant: 'info',
    },
  ],
  defaultVariants: {
    style: 'original',
  },
  variants: {
    rounded: { true: '' },
    style: {
      foundation:
        'h-[22px] gap-1.5 border-transparent bg-neutral-100 text-xs text-foreground shadow-[0_1px_3px_0_rgb(0_0_0_/_0.2)]',
      original: '',
    },
    variant: {
      // Keep in sync with the original badge variants
      default: '',
      destructive: '',
      info: '',
      outline: '',
      secondary: '',
      success: '',
      warning: '',
    },
  },
});

const extendedVariants = ({
  overrideClass = true,
  rounded,
  size,
  style,

  variant,
}: VariantProps<typeof originalBadgeVariants> &
  VariantProps<typeof variantsOverrides>) =>
  cx(
    originalBadgeVariants({
      overrideClass,
      rounded,
      size,
      variant,
    }),
    variantsOverrides({
      style,
      variant,
    }),
  );

export type BadgeProps = OriginalBadgeProps &
  VariantProps<typeof variantsOverrides> &
  VariantProps<typeof originalBadgeVariants> & {
    hasIndicator?: boolean;
  };
/**
 * ui-library Badge with the design-system foundation pill as the overridden
 * `default` variant, plus a `round` size for icon/number pills.
 *
 * Dims inside a disabled `<fieldset>` at the same `opacity-50` every control in
 * the library uses, so a locked section reads as one state rather than two.
 * The rule lives here rather than in the library's `Badge` (a pill is read-only
 * and as valid in a toolbar as in a form, so the primitive stays neutral) and
 * rather than at each call site, where it has to be remembered for every new
 * badge in a locked section — it was already missed on the two status pills in
 * `WorkspaceDetails/BusinessDetails/BusinessDetailsForm`. `fieldset:disabled`
 * only matches inside a disabled fieldset, so badges elsewhere are unaffected.
 */
export const Badge = ({
  children,
  className,
  hasIndicator,
  icon,
  overrideClass,
  rounded,
  size = 'default',

  style = 'original',
  variant,
  ...props
}: BadgeProps) => {
  const showIndicator = Boolean(hasIndicator && !icon);

  return (
    <OriginalBadge
      {...props}
      className={cn(
        extendedVariants({
          overrideClass,
          rounded,
          size,
          style,

          variant,
        }),
        '[fieldset:disabled_&]:opacity-50',
        className,
      )}
      extended
      icon={showIndicator ? null : icon}
      variant={variant}
    >
      {showIndicator && <BadgeIndicator variant={variant ?? undefined} />}

      {children}
    </OriginalBadge>
  );
};

const badgeIndicatorColouMap: Partial<
  Record<NonNullable<BadgeProps['variant']>, string>
> = {
  default: 'bg-muted-foreground',
  destructive: 'bg-ui-danger',
  info: 'bg-ui-info',
  success: 'bg-ui-success',
  warning: 'bg-ui-warning',
};

type BadgeIndicatorProps = {
  variant?: keyof typeof badgeIndicatorColouMap;
};

const BadgeIndicator = ({ variant = 'default' }: BadgeIndicatorProps) =>
  badgeIndicatorColouMap[variant] && (
    <span
      className={cn('size-[7px] rounded-full', badgeIndicatorColouMap[variant])}
    />
  );
